import React, { useContext, useState } from "react";
import { CURRENCY } from "utils/constants";
import Money from "components/money";
import { PatioContext } from "context/patio-calculator";
import awsFileUpload from "helpers/aws-upload";
import { FormValidateContext } from "@root/context/form-validate";
import generateProductSlipImage from "@root/helpers/image-w-measurements";
import { CartContext } from "@root/context/cart";
import buildCartTransformAttributes from "@root/helpers/cart-transform-attributes";
import QuantitySelector from "../../_components/quantity-selector";
import Button from "../../_components/button";
import { MEASUREMENTS_STATE } from "@root/constants/enums";

const ProductSummary = ({ product, quantity, handleQuantityChange, onAddToCart, isEditing, item }) => {
    const [load, setLoad] = useState(false);
    const { customAttributes, priceData, configuration, measurementsState } = useContext(PatioContext);
    const { validateForm, globalError } = useContext(FormValidateContext) || {};
    const { removeCartItem } = useContext(CartContext);

    const handleAddToCart = async () => {
        const isValid = validateForm();

        if (!isValid) return;

        const capturedImage = await generateProductSlipImage(
            product?.images?.edges?.[0]?.node?.url,
            product?.measurements,
            configuration,
        );

        const base64ToFile = (base64Data, fileName, mimeType = "image/png") => {
            const byteString = atob(base64Data.split(",")[1]);
            const uint8Array = new Uint8Array([...byteString].map((char) => char.charCodeAt(0)));

            return new File([uint8Array], fileName, { type: mimeType });
        };

        setLoad(true);

        const toProcess = Object.fromEntries(
            Object.entries(customAttributes)
                .filter(([key, value]) => key.startsWith("referenceImage") || (key.startsWith("c_") && value.constructor === File)),
        );

        const processedFiles = await awsFileUpload({
            ...toProcess,
            _productImage: base64ToFile(capturedImage, `${product?.title || "product"}-slip.png`),
        });

        const patioAttributes = Object.entries({
            ...customAttributes,
            ...processedFiles,
        })
            .map(([key, value]) => (value ? { key, value: value.toString() } : {}))
            .filter((item) => item?.value);

        patioAttributes.push({
            key: "_measurements",
            value: product?.measurements?.map((param) => param.name).join("|"),
        });

        patioAttributes.push({
            key: "__productData",
            value: JSON.stringify({
                _customProduct: "Yes",
                __productData: JSON.stringify({
                    ...priceData,
                    customization_variant_ids: priceData.customization_variant_ids.map((vid) => +vid),
                }),
                ...priceData.parameterValues,
            }),
        });

        patioAttributes.push({
            key: "_handle",
            value: product?.handle,
        });

        // Products on the Cart Transform list get their own attribute set; others keep the draft order one.
        const cartTransformAttributes = buildCartTransformAttributes({
            product, customAttributes, processedFiles, priceData,
        });

        if (isEditing && item?.node?.id) {
            await removeCartItem(item.node.id);
        }
        await onAddToCart(quantity, [], cartTransformAttributes || patioAttributes);
        setLoad(false);
    };

    return (
        <>
            <div className="product-page-patio-summary--desktop">
                <div className="product-page-patio-summary__inner">
                    <div className="product-page-patio-summary__inner-price">
                        <Money value={{ currencyCode: CURRENCY, amount: priceData?.totalPrice }} />
                    </div>
                    <div className="product-page-patio-summary__inner-qty">
                        <span className="product-page-patio-summary__inner-qty-title">Quantity</span>
                        <QuantitySelector
                            value={quantity}
                            onChange={handleQuantityChange}
                            maxValue={1000}
                            minValue={1}
                        />
                        <Button
                            variant="primary"
                            size="regular"
                            onClick={handleAddToCart}
                            disabled={load}
                            full
                        >
                            {load ? (isEditing ? "Updating Cart" : "Adding To Cart") : (isEditing ? "Update Cart" : "Add To Cart")}
                        </Button>
                        {globalError && (
                            <div className="product-page-patio-summary__error">
                                <p className="product-page-patio-summary__error-text">
                                    {globalError}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="product-page-patio-summary__inner-info">
                        <div className="product-page-patio-summary__inner-info-item">
                            <span className="product-page-patio-summary__inner-info-item-title">Free Shipping</span>
                            <p className="product-page-patio-summary__inner-info-item-text">on orders over $99</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* HIDE THE ADD TO CART BUTTON WHEN THE MEASUREMENTS ARE NOT ALL FILLED */}
            {measurementsState === MEASUREMENTS_STATE.FINISHED && (
                <div className="product-page-patio-summary--mobile">
                    <div className="product-page-patio-summary__inner--mobile">
                        <div className="product-page-patio-summary__inner-info--mobile">
                            <div className="product-page-patio-summary__inner-info-item--mobile">
                                <div className="product-page-patio-summary__inner-info-item-quantity">
                                    <p>Qty: </p>
                                    <QuantitySelector
                                        value={quantity}
                                        onChange={handleQuantityChange}
                                        maxValue={1000}
                                        minValue={1}
                                    />
                                </div>
                                <div className="product-page-patio-summary__inner-info-item-free-shipping">
                                    <div className="product-page-patio-summary__inner-info-item-free-shipping-wrapper">
                                        <span className="product-page-patio-summary__inner-info-item-title">Free Shipping</span>
                                        <p className="product-page-patio-summary__inner-info-item-text">on orders over $99</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="product-page-patio-summary__inner-cart--mobile">
                            <div className="product-page-patio-summary__inner-price--mobile">
                                <Money value={{ currencyCode: CURRENCY, amount: priceData?.totalPrice }} />
                            </div>
                            <div className="product-page-patio-summary__inner-button--mobile">
                                <Button
                                    size="large"
                                    full={true}
                                    onClick={handleAddToCart}
                                    disabled={load}
                                >
                                    {load ? "Adding To Cart" : isEditing ? "Update Cart" : "Add To Cart"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default ProductSummary;
